import { Link, NavLink, useLocation } from "react-router";
import { PanelLeftClose, PanelLeft, ShieldCheck } from "lucide-react";
import { adminNavGroups } from "./nav-items";
import { cn } from "@/lib/cn";

type Props = {
  collapsed: boolean;
  onToggle: () => void;
};

export function AdminSidebar({ collapsed, onToggle }: Props) {
  const { pathname } = useLocation();

  return (
    <aside
      className={cn(
        "sticky top-0 h-screen border-r border-admin-200 bg-admin-900 text-admin-100 transition-[width] duration-200 ease-out",
        collapsed ? "w-[72px]" : "w-[240px]",
      )}
    >
      <div className="flex h-16 items-center justify-between border-b border-admin-800 px-4">
        <Link to="/" className="flex items-center gap-2.5 overflow-hidden">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-500 text-white">
            <ShieldCheck className="h-4 w-4" />
          </div>
          {!collapsed && (
            <span className="text-[15px] font-bold text-white truncate">
              Admin
            </span>
          )}
        </Link>
        <button
          type="button"
          onClick={onToggle}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-admin-200 transition-colors hover:bg-admin-800 hover:text-white"
          title={collapsed ? "Expandir" : "Recolher"}
        >
          {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>
      </div>

      <nav className="flex h-[calc(100vh-4rem)] flex-col gap-6 overflow-y-auto p-3">
        {adminNavGroups.map((group, idx) => (
          <div key={idx} className="flex flex-col gap-1">
            {group.label && !collapsed && (
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-admin-200/60">
                {group.label}
              </p>
            )}
            {group.items.map((item) => {
              const Icon = item.icon;
              // Prefixo marca a seção (/usuarios/:id), a menos que outro item do menu seja mais específico
              // (/base/candidatos não acende "Base de mercado").
              const maisEspecifico = adminNavGroups.some((g) => g.items.some((o) => o.href.startsWith(item.href + "/") && pathname.startsWith(o.href)));
              const active =
                pathname === item.href ||
                (pathname.startsWith(item.href + "/") && item.href !== "/" && !maisEspecifico);
              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  title={collapsed ? item.label : undefined}
                  className={cn(
                    "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                    active
                      ? "bg-admin-800 font-medium text-white"
                      : "text-admin-200 hover:bg-admin-800/50 hover:text-white",
                    collapsed && "justify-center px-2",
                  )}
                >
                  <Icon
                    className={cn(
                      "h-[18px] w-[18px] shrink-0",
                      active ? "text-red-400" : "text-admin-200/70 group-hover:text-admin-100",
                    )}
                  />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>
    </aside>
  );
}
